<template>
  <div
    class="fts-detail-view-form"
    :data-detail-view-form-load="formLoading || formSubmitting ? 'loading': 'loaded'"
    v-if="
      (header && header.id) || 
      (selectedActivity && selectedActivity.id == 'create')
    "
  >
    <div class="dv-form">
      <div class="dv-form__header" v-if="!isError">
        <div class="dv-form-header">
          <div class="dv-form-header-wrapper">
            <fts-tippy
              :key="formTitle"
              :content="formTitle"
              append-trigger-class="dv-form-title"
              class="dv-form-title-wrapper"
            >
              {{ formTitle }}
            </fts-tippy>
          </div>

          <div class="dv-form-header-activity" v-if="selectedActivity && selectedActivity.standard && selectedActivity.id === 'create'">
            <div class="dv-form-header-activity-create">
              <div class="dv-form-header-activity-create-label">
                New
              </div>
              <fts-label-group
                :item="{ 'name': module.name, 'emoji': module.emoji }"
                emoji
                :emoji-size="14"
                v-if="module && module.name"
              />
            </div>
          </div>

          <div class="dv-form-header-wrapper" v-else-if="!selectedActivity">
            <fts-tippy
              :content="header.state && header.state.name ? header.state.name : '(no state)'"
              class="dv-form-state-wrapper"
              v-if="isChangeStatusRight && states && states.length"
            >
              <fts-states-design-dropdown
                :states="states"
                :selected-state="header.state"
                blank-option-label="no state"
                disable-blank-option
                disable-assign-design
                :design-mode="designMode"
                @option:select="(val) => changeState(val, 'select')"
                @option:create="(val) => changeState(val, 'create')"
                @option:clear="changeState()"
                @state:update="(val) => updateState(val)"
                @state:delete="(val) => deleteState(val)"
              />
            </fts-tippy>
            <fts-tippy
              :content="header.state && header.state.name"
              class="dv-form-state-wrapper"
              v-else-if="header.state && header.state.id"
            >
              <fts-state
                :state="header.state"
                :states="states"
                class="dv-form-state"
              />
            </fts-tippy>
            <!-- audit -->
            <fts-tippy 
              :options="{ placement: 'bottom-start' }" 
              append-trigger-class="audit-group__trigger" 
              append-content-class="audit-group__content" 
              class="fts-audit-group"
              v-if="header.audit && header.audit.updatedBy"
            >
              <template>
                <fts-profile :user-name="header.audit.updatedBy" />
                <div class="audit-group-date">
                  {{moment(header.audit.updatedDate).fromNow()}}
                </div>
              </template>

              <template slot="content">
                <fts-profile :profilePhotoSize="26" :user-name="header.audit.updatedBy" class="audit-group-wrapper">
                  <div slot="profileName" slot-scope="{ userProfileName }" class="audit-group-info">
                    <div>{{userProfileName}}</div>
                    <div class="audit-group-date">updated on {{moment(header.audit.updatedDate).format('llll')}}</div>
                  </div>
                </fts-profile>

                <fts-profile :profilePhotoSize="26" :user-name="header.audit.createdBy" class="audit-group-wrapper">
                  <div slot="profileName" slot-scope="{ userProfileName }" class="audit-group-info">
                    <div>{{userProfileName}}</div>
                    <div class="audit-group-date">created on {{moment(header.audit.createdDate).format('llll')}}</div>
                  </div>
                </fts-profile>
              </template>
            </fts-tippy>
          </div>
        </div>

        <div class="dv-form-header-action">
          <fts-form-sync
            :synchronize="$refs.activityClassification.synchronize"
            class="dv-form-header-action-item"
            v-if="
              selectedActivity &&
              !(['assign', 'changeStatus', 'duplicate', 'delete'].indexOf(selectedActivity.id) > -1) &&
              $refs.activityClassification
            "
          />

          <fts-assign-to
            :module-id="moduleId"
            :form-data.sync="formData"
            :max-length="3"
            avatar-mode
            :avatar-size="26"
            :view-mode="!isAssignRight"
            :toggle-create="!(formData.assignees && formData.assignees.length)"
            :comment-section="isCommentRight"
            class="dv-form-header-action-item"
            @refresh="$emit('refresh-list')"
            v-if="!selectedActivity && header && header.id && formData"
          />

          <div
            class="dv-form-history"
            :class="{'dv-form-history--count': header.commentCount}"
            v-if="isHistoryRight && header && !isMultiple"
          >
            <fts-tippy :content="`${isCommentRight ? 'Comment' : 'History'}`">
              <fts-btn
                text
                icon
                :color="header && (header.collaborationFlags & 4) === 4 ? 'primary' : 'default'"
                class="dv-form-header-action-btn"
                @click="toggleHistory"
              >
                <font-awesome-icon :icon="['far', 'comment-alt']" />
              </fts-btn>
            </fts-tippy>
            <div class="dv-form-history-count" v-if="header.commentCount">
              {{ header.commentCount > 99 ? 99 :header.commentCount }}
            </div>
          </div>

          <fts-tippy content="Actions" v-if="!isMultiple">
            <fts-dropdown-select
              append-to-body
              placement="bottom-end"
              prevent-overflow-alt-axis
              prevent-overflow-tether-off
              :offset="[12, 3]"
            >
              <fts-btn
                slot="toggle"
                text
                icon
                class="dv-form-header-action-btn"
              >
                <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
              </fts-btn>
              <template #menu="{ select }" v-if="formActions.length">
                <template v-for="option in formActions">
                  <!-- divider -->
                  <div :key="`divider-${option.id}`" class="dropdown-divider" v-if="option.optionRole === 'divider'"></div>
                  <!-- share -->
                  <div
                    :key="option.id"
                    class="dropdown-option"
                    @click="
                      select();
                      $refs.ftsShareFormModal.show({
                        id: header.id,
                        moduleId: moduleId,
                        activityId: selectedActivity ? selectedActivity.id : 'view'
                      })
                    "
                    v-else-if="option.actionType == 'standard' && option.id == 'share'"
                  >
                    <div class="dropdown-option-icon">
                      <font-awesome-icon :icon="['fas', 'share-alt']" />
                    </div>
                    <div>Share</div>
                  </div>
                  <!-- copy link -->
                  <div
                    :key="option.id"
                    class="dropdown-option"
                    @click="select();entryCopyLink();"
                    v-else-if="option.actionType == 'standard' && option.id == 'copyLink'"
                  >
                    <div class="dropdown-option-icon">
                      <font-awesome-icon :icon="['far', 'clone']" />
                    </div>
                    <div>Copy link</div>
                  </div>
                  <!-- embed form -->
                  <div
                    :key="option.id"
                    class="dropdown-option"
                    @click="
                      select();
                      $refs.ftsEmbedFormModal.show({
                        id: header.id,
                        moduleId: moduleId,
                        activityId: selectedActivity ? selectedActivity.id : 'view'
                      })
                    "
                    v-else-if="option.actionType == 'standard' && option.id == 'embedForm'"
                  >
                    <div class="dropdown-option-icon">
                      <font-awesome-icon :icon="['fas', 'code']" />
                    </div>
                    <div>Embed form <fts-release-badge alpha class="fts-align-top" /></div>
                  </div>
                  <!-- manage entry -->
                  <fts-tippy
                    :key="option.id"
                    append-trigger-class="dropdown-option dropdown-option--secondary"
                    :content="option.tooltip"
                    @trigger:click="select();executeFormAction(option, option.actionType)"
                    v-else-if="option.actionType == 'standard' && option.id == 'manage'"
                  >
                    <template>
                      <div class="dropdown-option-icon">
                        <i :class="option.icon"></i>
                      </div>
                      <div>{{option.name}}</div>
                    </template>
                  </fts-tippy>
                  <!-- default -->
                  <div
                    :key="option.id"
                    class="dropdown-option"
                    @click="select();executeFormAction(option, option.actionType)"
                    v-else
                  >
                    <div class="dropdown-option-icon" v-if="option.icon">
                      <i :class="option.icon"></i>
                    </div>
                    <div class="dropdown-option-emoji" v-else-if="option.emoji">
                      <fts-moji :emoji="option.emoji" :size="14" />
                    </div>
                    <div class="dropdown-option-icon" v-else-if="option.actionType === 'template'">
                      <font-awesome-icon :icon="['far', 'file-pdf']" class="fts-file-pdf" v-if="option.typeId === 'pdf'" />
                      <font-awesome-icon :icon="['far', 'file-alt']" class="fts-file-excel" v-else-if="option.typeId === 'spreadsheet'" />
                      <font-awesome-icon :icon="['far', 'file-code']" class="fts-file-html" v-else-if="option.typeId === 'html'" />
                      <font-awesome-icon :icon="['far', 'file']" class="fts-file-default" v-else />
                    </div>
                    <div :style="{'color': option.color}">
                      {{ option.name }}
                    </div>
                  </div>   
                </template>
              </template>
            </fts-dropdown-select>
          </fts-tippy>
        </div>
      </div>

      <div class="dv-form__content fts-scrollbar">
        <div
          class="dv-form-action-detail"
          v-if="
            selectedActivity && 
            selectedActivity.id && 
            selectedActivity.id !== 'create' && 
            !isMultiple && 
            !isError 
          "
        >
          <fts-btn
            text
            text-transform-init
            class="fts-font-normal"
            @click="toggleActivityViewForm"
          >
            {{isActivityViewForm ? 'hide' : 'show'}} details
          </fts-btn>
        </div>

        <fts-transition-expand>
          <div class="dv-form-error" v-if="viewFormError && (!selectedActivity || this.isActivityViewForm)">
            <div class="dv-form-error-icon">
              <font-awesome-icon :icon="['fas', 'times']" fixed-width />
            </div>
            <div class="dv-form-error-title">
              Something went wrong
            </div>
            <div class="dv-form-error-description">
              {{viewFormError}}
            </div>
          </div>

          <fts-classification
            :locus-id="header && header.id"
            :vector-id="moduleId"
            :users="users"
            :username="username"
            :activity-id="this.fullView ? 'view' : 'quickView'"
            :view="true"
            :view-data="viewData"
            :loading="formLoading"
            :design-mode="designMode && !this.isActivityViewForm"
            v-else-if="!selectedActivity || this.isActivityViewForm"
          />
        </fts-transition-expand>

        <div class="dv-form-separator" v-if="isActivityViewForm && selectedActivity && selectedActivity.id">
          <fts-label-group
            :item="selectedActivity"
            v-if="selectedActivity.standard"
          />
          <fts-label-group
            :item="selectedActivity"
            emoji
            :emoji-size="16"
            :label-color="selectedActivity.color"
            v-else
          />
          <div class="dv-form-separator-line"></div>
        </div>

        <template v-if="selectedActivity && selectedActivity.id">
          <fts-transition-expand>
            <div class="dv-form-error" v-if="activityFormError">
              <div class="dv-form-error-icon">
                <font-awesome-icon :icon="['fas', 'times']" fixed-width />
              </div>
              <div class="dv-form-error-title">
                Something went wrong
              </div>
              <div class="dv-form-error-description">
                {{activityFormError}}
              </div>
            </div>

            <div 
              class="dv-form-activity" 
              v-else-if="['assign', 'changeStatus', 'duplicate', 'delete'].includes(selectedActivity.id)" 
            >
              <div class="dv-form-activity-content">
                <div class="dv-form-activity-label fts-text-center" v-if="selectedActivity.id == 'delete'">
                  Are you sure to delete?
                </div>

                <div class="dv-form-activity-label fts-text-center" v-else-if="selectedActivity.id == 'duplicate'">
                  Are you sure to duplicate?
                </div>

                <div class="dv-form-activity-state" v-else-if="selectedActivity.id == 'changeStatus'">
                  <div class="dv-form-activity-label-group">
                    <div class="dv-form-activity-label">
                      Change state to
                    </div>

                    <fts-dropdown-select
                      append-to-body
                      prevent-overflow-alt-axis
                      prevent-overflow-tether-off
                      v-model="formData.selectedState"
                      :options="states"
                    >
                      <fts-state
                        slot="toggle"
                        slot-scope="{ selectedOption }"
                        :state="
                          selectedOption && selectedOption.name ?
                            selectedOption :
                            { name: 'select state' }
                        "
                        :states="states"
                      />

                      <div
                        slot="option"
                        slot-scope="{ option, select }"
                        class="dropdown-option"
                        @click="select(option)"
                      >
                        <fts-state :state="option" />
                      </div>

                    </fts-dropdown-select>
                  </div>

                  <div
                    class="fts-form-error"
                    v-if="
                      $v.formData &&
                      $v.formData.selectedState &&
                      $v.formData.selectedState.$error
                    "
                  >
                    State is required.
                  </div>
                </div>

                <fts-assign
                  :assign-data="formData"
                  v-else-if="selectedActivity.id == 'assign'"
                />
              </div>

              <fts-comment
                :vector-id="moduleId"
                :comment="comment"
                v-if="isCommentRight && selectedActivity.id !== 'duplicate'"
              />
            </div>

            <fts-classification
              :locus-id="header && header.id"
              :vector-id="moduleId"
              :users="users"
              :username="username"
              :activity="selectedActivity.name"
              :activity-id="selectedActivity.id"
              :view="!isCapableOf(SYSTEM_CAPABILITY.Write)"
              :comment="comment"
              :comment-section="isCommentRight"
              :loading="formLoading"
              :design-mode="designMode"
              @submit="submit"
              ref="activityClassification"
              v-else
            />
          </fts-transition-expand>

          <div class="dv-form-afooter" v-if="!formLoading && !isError">
            <fts-workflow-bar
              :module-id="moduleId"
              :states="states"
              :activities="canvasActivities"
              :activity.sync="selectedActivity"
              :current-state="header.state"
              :next-states.sync="activityFormNextStates"
              :form-data.sync="formData"
              :assign-right="isAssignRight"
              :comment-right="isCommentRight"
              :design-mode="designMode"
              class="dv-form-afooter-activity-flow"
              @activity:delete="cancelActivityForm"
              v-if="selectedActivity"
            />

            <fts-form-footer
              :activity="selectedActivity"
              :sync-state="$refs.activityClassification && $refs.activityClassification.syncState"
              :loading="$refs.activityClassification && $refs.activityClassification.isFormLoading"
              :submitting="formSubmitting"
              :enable-continuable-activity="true"
              :enable-activity-info="true"
              :is-activity-continuable.sync="isActivityContinuable"
              class="dv-form-afooter-action"
              @synchronize="evt => ($refs.activityClassification && $refs.activityClassification.synchronize(evt))"
              @sync:submit="$refs.activityClassification && $refs.activityClassification.submit()"
              @submit="submit"
              @cancel="cancelActivityForm"
            />
          </div>
        </template>
      </div>

      <div class="dv-form__footer" v-if="!formLoading && !isError && !selectedActivity">
        <fts-form-footer-action
          :activities="footer.activities"
          :customs="footer.customs"
          :documents="footer.documents"
          :canvas-activities="canvasActivities"
          :design-mode="designMode"
          @execute:activity="evt => executeFormAction(evt, 'activity')"
          @execute:custom="evt => executeFormAction(evt, 'custom')"
          @execute:document="evt => executeFormAction(evt, 'template')"
          @create:activity="evt => createActivity(evt)"
          v-if="
            (
              (footer.activities && footer.activities.length) ||
              (footer.customs && footer.customs.length) ||
              (footer.documents && footer.documents.length)
            ) || designMode
          "
        />
      </div>
    </div>

    <fts-history
      :key="header.id"
      :locus-id="header.id"
      :vector-id="moduleId"
      :states="states"
      :users="users"
      :comment-section="isCommentRight"
      :init-open="getHistoryInitOpen()"
      @added="historyAdded"
      ref="ftsHistory"
      v-if="
        isHistoryRight &&
        !isMultiple &&
        !isError
      "
    />

    <div class="dv-form-loading-overlay" v-if="formLoading || formSubmitting">
      <fts-spinner show :class="{'dv-form-loading-offset-r': Boolean($refs.ftsHistory && $refs.ftsHistory.show)}"/>
    </div>

    <fts-action-page-modal ref="ftsActionPageModal" />
    <fts-document-form-modal ref="ftsDocumentFormModal" />
    <fts-form-message-modal ref="ftsFormMessageModal" />
    <fts-share-form-modal ref="ftsShareFormModal" />
    <fts-embed-form-modal ref="ftsEmbedFormModal" />
  </div>
</template>

<script>
import axios from 'axios';
import Vue from 'vue';
import moment from 'moment';
import firebase from 'firebase/app';
import 'firebase/analytics';
import Vuelidate from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
Vue.use(Vuelidate);

import canvasDesign from '../../commons/canvasDesign';
import commons from '../../commons/commons';
import confetti from '../../commons/confetti';
import { isCapableOf, SYSTEM_CAPABILITY } from '../../commons/flags';
import { authorizationStore } from '../../store/authorizationStore';
import { classificationStore } from '../../store/classificationStore';
import { clientStore } from '../../store/clientStore';
import { store as canvasDesignStore } from '../../store/canvasDesignStore';
import LocalStorageService from '../../store/LocalStorageService';

import FTSActionPageModal from '../FTSActionPageModal';
import FTSAssign from '../FTSAssign';
import FTSAssignTo from '../FTSAssignTo';
import FTSBtn from '../FTSBtn';
import FTSClassification from '../FTSClassification';
import FTSComment from '../FTSComment';
import FTSDocumentFormModal from '../FTSDocumentFormModal';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSEmbedFormModal from '../FTSEmbedFormModal';
import FTSFormFooter from '../form/FTSFormFooter';
import FTSFormFooterAction from '../form/FTSFormFooterAction';
import FTSFormMessageModal from '../form/FTSFormMessageModal';
import FTSFormSync from '../form/FTSFormSync';
import FTSHistory from '../history/FTSHistory';
import FTSLabelGroup from '../FTSLabelGroup';
import FTSModal from '../FTSModal';
import FTSMoji from '../FTSMoji';
import FTSProfile from '../FTSProfile';
import FTSShareFormModal from '../FTSShareFormModal';
import FTSSpinner from '../FTSSpinner';
import FTSState from '../FTSState';
import FTSStatesDesignDropdown from '../../design/FTSStatesDesignDropdown';
import FTSTippy from '../FTSTippy';
import FTSTransitionExpand from '../FTSTransitionExpand';
import FTSWorkflowBar from '../FTSWorkflowBar';

const MODULE_UNAVAILABLE_MESSAGE = "Module you trying to access is unpublished or have been removed.";
const ENTRY_UNAVAILABLE_MESSAGE = "Entry you trying to access is not available. Please check with your administrator.";
const ACTIVITY_UNAVAILABLE_MESSAGE = "Entry not able to perform this activity. Please check with your administrator";
const CAPABILITY_UNAVAILABLE_MESSAGE = "401 Unauthorized. Access denied due to invalid credentials.";

const getSuccessMessage = (id, standard) => {
  if (standard) {
    if (id === 'create')
      return 'Successfully created';
    else if (id == 'edit')
      return 'Successfully updated';
    else if (id === 'delete')
      return 'Successfully deleted';
    else if (id === 'duplicate')
      return 'Successfully duplicated';
    else if (id === 'assign')
      return 'Successfully assigned';
  }
  return 'Submission successful';
}
const getErrorMessage = (id, standard) => {
  if (standard) {
    if (id === 'create')
      return 'Failed to create';
    else if (id === 'edit')
      return 'Failed to update';
    else if (id === 'delete')
      return 'Failed to delete';
    else if (id === 'duplicate')
      return 'Failed to duplicate';
    else if (id === 'assign')
      return 'Failed to assign';
  }
  return 'Submission failed';
}

export default {
  components: {
    'fts-action-page-modal': FTSActionPageModal,
    'fts-assign': FTSAssign,
    'fts-assign-to': FTSAssignTo,
    'fts-btn': FTSBtn,
    'fts-classification': FTSClassification,
    'fts-comment': FTSComment,
    'fts-document-form-modal': FTSDocumentFormModal,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-embed-form-modal': FTSEmbedFormModal,
    'fts-form-footer': FTSFormFooter,
    'fts-form-footer-action': FTSFormFooterAction,
    'fts-form-message-modal': FTSFormMessageModal,
    'fts-form-sync': FTSFormSync,
    'fts-history': FTSHistory,
    'fts-label-group': FTSLabelGroup,
    'fts-modal': FTSModal,
    'fts-moji': FTSMoji,
    'fts-profile': FTSProfile,
    'fts-share-form-modal': FTSShareFormModal,
    'fts-spinner': FTSSpinner,
    'fts-state': FTSState,
    'fts-states-design-dropdown': FTSStatesDesignDropdown,
    'fts-tippy': FTSTippy,
    'fts-transition-expand': FTSTransitionExpand,
    'fts-workflow-bar': FTSWorkflowBar
  },
  props: {
    listData: Array,
    designMode: Boolean
  },
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    moduleId: null,
    listingId: null,
    header: {},
    footer: {},
    selectedActivity: null,
    username: null,
    fullView: false,
    viewData: null,
    isActivityViewForm: false,
    isActivityContinuable: false,
    expandFormAction: false,
    formData: {},
    comment: {},
    entries: [],
    items: [],
    activityFormNextStates: [],
    viewFormError: null,
    activityFormError: null,
    formLoading: false,
    formSubmitting: false,
  }),
  validations() {
    let formData = {};
    if (this.selectedActivity && this.selectedActivity.id == 'changeStatus') {
      formData = {
        selectedState: {
          name: { required }
        }
      }
    }
    return { formData };
  },
  methods: {
    moment,
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    calendarRelativeTime(date) {
      return commons.calendarRelativeTime(date);
    },
    isCapableOf(type) {
      return isCapableOf(type, clientStore.getters.workspaceModules, this.moduelId);
    },
    isRightOf(type) {
      return type &&
        this.footer &&
        this.footer.standards &&
        this.footer.standards.indexOf(type) > -1;
    },
    // to-do fix route NavigationDuplicated
    setRouter() {
      const setRouteQuery = (query) => {
        if (this.fullView) {
          query.m = "f";
        } else if (query.hasOwnProperty("m")) {
          delete query.m;
        }
      }

      if (this.selectedActivity && this.selectedActivity.id === 'create') {
        let query = {
          ...this.$route.query,
          a: 'create'
        };
        if (query.hasOwnProperty('id')) {
          delete query.id;
        }
        this.$router.push({ query: query });
      } else if (this.header && this.header.id) {
        let query = {
          ...this.$route.query,
          id: commons.queryEncodeids(this.moduleId, this.header.id)
        }

        setRouteQuery(query);

        if (this.selectedActivity) {
          query.a = this.selectedActivity.id;
        } else if (query.hasOwnProperty("a")) {
          delete query.a;
        }

        this.$router.push({ query: query });
      } else {
        let route = {
          name: 'list-view',
          params: {
            moduleId: commons.encodeids(this.moduleId)
          }
        }
        if (this.listingId) {
          route.query = { l: this.listingId };
        } 
        this.$router.push(route);
      }
    },
    setEntryHeader(data) {
      if (Array.isArray(data) && data.length > 0 && commons.isObject(data[0])) {
        this.header = data[0];
        this.entries = data;
      } else if (commons.isObject(data)) {
        this.header = data;
        this.entries = [data];
      } else {
        this.header = {};
        this.entries = [];
      }
    },
    initialFormData() {
      this.formData = {
        id: this.header.id,
        assignees: this.header.assignees,
        due: this.header.due
      }
    },
    async loadViewForm() {
      this.formLoading = true;
      try {
        if (!this.isCapableOf(SYSTEM_CAPABILITY.Read)) {
          throw { response: { data: { message: CAPABILITY_UNAVAILABLE_MESSAGE }, status: 400 }};
        }
        const res = await classificationStore.dispatch('loadViewForm', {
          moduleId: this.moduleId ? this.moduleId.toString() : null,
          listingId: this.listingId,
          activityId: this.fullView ? 'view' : 'quickView',
          entryId: this.header.id,
          locusId: this.header.id,
          query: this.$route.query,
          users: this.users
        });

        if (this.header.id != res.header.entryId) {
          console.warn(`Form view aborted: Entry mismatch detected. Expected document ID '${this.header.documentId}', but received '${res.header.documentId}'.`);
          return;
        }

        this.header = res.header;
        this.footer = res.footer;
        this.viewData = res.viewData;
        !this.isActivityViewForm && this.initialFormData();
        this.items = [{
          ...res.header, 
          extension: {...res.viewData && res.viewData.formData}
        }];

        if (Array.isArray(this.footer && this.footer.standards)) {
          let excludedStandards = [];

          if (!this.isCapableOf(SYSTEM_CAPABILITY.Write)) {
            excludedStandards.push(...["create", "edit", "changeStatus", "duplicate", "assign"]);
          }

          if (!this.isCapableOf(SYSTEM_CAPABILITY.Delete)) {
            excludedStandards.push("delete");
          }

          this.footer.standards = this.footer.standards.filter(x => !excludedStandards.includes(x));
        }

        if (Array.isArray(this.footer && this.footer.customs) && this.footer.customs.length) {
          this.footer.customs = commons.getFormAvailableCustoms(this.footer.customs, this.items);
        }

        this.viewFormError = null;
      } catch(err) {
        console.error('loadViewForm', err);
        if (
          err.response &&
          err.response.status == 400 &&
          err.response.data &&
          err.response.data.message
        ) {
          this.viewFormError = err.response.data.message;
        } else {
          this.viewFormError = ENTRY_UNAVAILABLE_MESSAGE;
        }
      } finally {
        this.formLoading = false;
      }
    },
    async loadActivityForm() {
      this.$v.$reset();
      this.comment = {};
      this.activityFormNextStates = [];
      this.formLoading = true;
      this.isActivityViewForm = false;
      try {
        if (this.selectedActivity.id === "manage" && !this.isAdminUser) {
          throw { response: { data: { message: ACTIVITY_UNAVAILABLE_MESSAGE }, status: 400 }};
        }

        if (
          (this.selectedActivity.id === "delete" && !this.isCapableOf(SYSTEM_CAPABILITY.Delete)) ||
          (
            ["create", "edit", "changeStatus", "duplicate", "assign"].indexOf(this.selectedActivity.id) > -1 &&
            !this.isCapableOf(SYSTEM_CAPABILITY.Write)
          )
        ) {
          throw { response: { data: { message: CAPABILITY_UNAVAILABLE_MESSAGE }, status: 400 }};
        }

        const setForm = (result) => {
          this.header = result.header;
          this.footer = result.footer;
          this.initialFormData();
          // activity
          if (result.header && result.header.activity) {
            this.selectedActivity = result.header.activity;
          }
          // form sync
          if (result.needSync && this.selectedActivity) {
            this.$refs.activityClassification.pollData(this.selectedActivity);
          }
          // states
          if (this.states && this.states.length) {
            if (Array.isArray(this.footer.states) && this.footer.states.length) {
              this.activityFormNextStates = this.footer.states.map(x => this.states.find(y => y.id == x)).filter(y => y);
            }
          }
          // assignees
          if (Array.isArray(this.footer.assignees) && this.footer.assignees.length) {
            this.formData.assignees = this.footer.assignees;
          }
          // analytic
          if (this.module) {
            firebase.analytics().logEvent("screen_view", {
              screen_name: `${this.selectedActivity.name}|${this.header.documentId}|${this.module.name}`,
            });
          } else {
            throw { response: { data: { message: MODULE_UNAVAILABLE_MESSAGE }, status: 400 }};
          }
        }

        let payload = {
          moduleId: this.moduleId ? this.moduleId.toString() : null,
          listingId: this.listingId,
          activityId: this.selectedActivity.id,
          entryId: this.header.id,
          locusId: this.header.id,
          query: this.$route.query,
          users: this.users,
          enableCache: true,
          hasCache: false
        }

        if (this.selectedActivity.id == "create") {
          const cachedForm = classificationStore.getters.getCachedForm({
            moduleId: this.moduleId,
            activityId: this.selectedActivity.id
          });

          if (cachedForm) {
            classificationStore.dispatch("loadActivityFormCache", {
              setup: payload,
              data: cachedForm
            });
            setForm(cachedForm);
            payload.hasCache = true;
          }
        }

        if (payload.hasCache) {
          this.activityFormError = null;
          this.formLoading = false;
        }

        const result = await classificationStore.dispatch('loadActivityForm', payload);
        if (this.header.id != result.header.entryId) {
          console.warn(`Form activity aborted: Entry mismatch detected. Expected document ID '${this.header.documentId}', but received '${result.header.documentId}'.`);
          return;
        }

        setForm(result);

        if (result.needCache) {
          classificationStore.dispatch("setCachedForm", {
            moduleId: this.moduleId,
            activityId: this.selectedActivity.id,
            data: result
          });
        } else {
          classificationStore.dispatch("clearCachedForm", {
            moduleId: this.moduleId,
            activityId: this.selectedActivity.id
          });
        }

        this.activityFormError = null;
        this.formLoading = false;
      } catch(err) {
        console.error('loadActivityForm', err);

        if (axios.isCancel(err)) {
          return;
        }

        if (
          err.response &&
          err.response.status == 400 &&
          err.response.data &&
          err.response.data.message
        ) {
          this.activityFormError = err.response.data.message;
        } else {
          this.activityFormError = ACTIVITY_UNAVAILABLE_MESSAGE;
        }

        this.formLoading = false;
      }
    },
    async submit(formData) {
      if (this.selectedActivity && this.selectedActivity.id == 'changeStatus') {
        this.$v.$touch();
        if (this.$v.$invalid) {
          return;
        }
      }

      try {
        this.formSubmitting = true;

        if (
          this.designMode &&
          this.isAdminUser &&
          this.formData &&
          this.formData.selectedState &&
          this.formData.selectedState.id
        ) {
          let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
          if (
            !Array.isArray(canvas.states) ||
            !canvas.states.some((x) => x.id === this.formData.selectedState.id)
          ) {
            try {
              clientStore.dispatch("setDesignModeLoading", true);
              canvasDesign.setState(canvas, this.formData.selectedState);
              canvasDesign.compactDesignBeforeSave(canvas);
              canvasDesignStore.dispatch("updateVectorCanvas", canvas);
              const moduleResponse = await canvasDesignStore.dispatch("saveVector");
              clientStore.dispatch("setWorkspaceModuleStates", {
                moduleId: moduleResponse.data.id,
                states: canvas.states
              });
              clientStore.dispatch("setDesignModeState", {
                success: true,
                updatedOn: moduleResponse.data.updatedDate
              });
            } catch(err) {
              console.error(err);
              clientStore.dispatch("setDesignModeState", { error: true });
              throw err;
            } finally {
              clientStore.dispatch("setDesignModeLoading", false);
            }
          }
        }

        const res = await classificationStore.dispatch('submitActivity', {
          moduleId: (this.module && this.module.id)? this.module.id.toString() : null,
          activityId: this.selectedActivity.id,
          payload: formData,
          entryIds: this.entries.map((x) => x.id),
          post: this.comment,
          intention: this.comment.attention,
          stateId: this.formData.selectedState && this.formData.selectedState.id ?
            this.formData.selectedState.id :
            null,
          assignees: this.formData.assignees,
          due: this.formData.due ? moment(this.formData.due).endOf('day') : null,
          query: this.$route.query
        });

        if (
          this.isActivityContinuable && 
          this.selectedActivity && 
          this.selectedActivity.id && 
          !["create"].includes(this.selectedActivity.id)
        ) {
          this.$emit("activity:selected", this.selectedActivity);
        } else {
          this.$emit("activity:selected", null);
        }

        this.formSubmitting = false;
        /* confetti */
        if (res.data.celebrate) {
          confetti();
        }
        /* message */
        if (res.data.message) {
          await this.$refs.ftsFormMessageModal.show(res.data.message);
        } else {
          this.$toasted.success(
            getSuccessMessage(this.selectedActivity.id, this.selectedActivity.standard)
          );
        }
        /* form ads */
        if (
          classificationStore.state.form &&
          classificationStore.state.form.ads
        ) {
          let entryId = result.data.id;
          try {
            eval(classificationStore.state.form && classificationStore.state.form.ads);
          } catch (ex) {
            this.$toasted.error(ex);
          }
        }

        const cloneHeader = _.cloneDeep(this.header);
        classificationStore.dispatch("resetActivityForm");
        this.selectedActivity = null;
        this.viewData = null;
        this.setEntryHeader();
        this.footer = {};
        this.setRouter();
        this.$emit('refresh-list', { previousEntry: cloneHeader });
      } catch(err) {
        console.error('submit', err);
        let message = getErrorMessage(this.selectedActivity.id, this.selectedActivity.standard);
        if (
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          message = `${message}. ${err.response.data.message}`;
        }
        this.formSubmitting = false;
        this.$toasted.error(message);
      }
    },
    async cancelActivityForm() {
      this.selectedActivity = null;
      this.isActivityContinuable = false;

      if (this.fullView === true && !this.isViewRight) {
        this.fullView = false;
      } else if (this.fullView !== true && !this.isQuickViewRight) {
        this.fullView = true;
      }

      this.$emit("activity:selected", {
        id: this.fullView ? "view" : "quickView",
        standard: true
      });

      if (this.header.id) {
        this.setEntryHeader(this.header);
        this.setRouter();
        await this.loadViewForm();
      } else if (this.listData && this.listData[0] && this.listData[0].id) {
        this.setEntryHeader(this.listData[0]);
        this.setRouter();
        await this.loadViewForm();
      } else {
        this.setEntryHeader();
        this.footer = {};
        this.viewData = null;
        this.setRouter();
      }
    },
    async toggleActivityViewForm() {
      this.isActivityViewForm = !this.isActivityViewForm;
      if (this.viewData == null) {
        await this.loadViewForm();
      }
    },
    toggleHistory() {
      if (this.$refs.ftsHistory) {
        this.$refs.ftsHistory.toggleHistory();
        LocalStorageService.setModuleListingPreference(
          this.moduleId,
          this.listingId,
          {
            ...LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId),
            historyVisible: Boolean(this.$refs.ftsHistory.show)
          }
        );
      }
    },
    historyAdded() {
      if (this.header.commentCount) {
        this.header.commentCount += 1;
      } else {
        this.header.commentCount = 1;
      }
      this.$emit('refresh-list');
    },
    getHistoryInitOpen() {
      const localStorageValue = LocalStorageService.getModuleListingPreference(this.moduleId, this.listingId);
      return !(localStorageValue && localStorageValue.historyVisible == false);
    },
    executeFormAction(action, actionType) {
      if (actionType == "standard" || actionType == "activity") {
        this.selectedActivity = action;

        if (this.selectedActivity.id == "create") {
          this.setEntryHeader();
          this.footer = {};
        }

        this.setRouter();
        this.loadActivityForm();
      } else if (actionType == "custom") {
        commons.executeEntryCustom({
          action: action,
          items: this.items,
          modal: this.$refs.ftsActionPageModal
        });
      } else if (actionType == "document" || actionType == "template") {
        if (action.hasForm) {
          this.$refs.ftsDocumentFormModal.show({
            moduleId: this.moduleId,
            listingId: this.listingId,
            document: action,
            items: this.items
          });
        } else {
          window.eventBus.$emit("showPDFDocument", {
            document: action,
            items: [{ id: this.header.id }],
            moduleId: this.moduleId,
          });
        }
      } else {
        console.warn(`Unhandled action type: ${actionType}`);
      }
    },
    async createActivity(x) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const activity = canvasDesign.initActivity(x);
        canvasDesign.setActivity(canvas, activity);
        canvasDesign.compactDesignBeforeSave(canvas);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        const res = await canvasDesignStore.dispatch("saveVector");
        this.executeFormAction(activity, 'activity');
        clientStore.dispatch("setDesignModeState", {
          success: true,
          updatedOn: res.data.updatedDate
        });
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to create activity'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async changeState(item, action) {
      try {
        this.formSubmitting = true;
        if (action === "create") {
          let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
          let state = canvasDesign.initState(item);
          this.$set(this.header, "state", state);
          canvasDesign.setState(canvas, state);
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          this.$set(this.header, "state", item);
        }

        await classificationStore.dispatch("submitActivity", {
          moduleId: (this.module && this.module.id)? this.module.id.toString() : null,
          activityId: "changeStatus",
          entryIds: [this.header.id],
          state: item.name
        });

        this.selectedActivity = null;
        this.setEntryHeader();
        this.footer = {};
        this.setRouter();
        this.$emit('refresh-list');
      } catch(err) {
        console.error('submit', err);
        let message = getErrorMessage("changeStatus", true);
        if (
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          message = `${message}. ${err.response.data.message}`;
        }
        this.$toasted.error(message);
      } finally {
        this.formSubmitting = false;
      }
    },
    async updateState(item) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, item.id);
        if (state) {
          canvasDesign.setState(canvas, {
            ...state,
            name: item.name,
            color: item.color
          });
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'state not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to update state'
        }); 
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async deleteState(id) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, id);
        if (state) {
          canvasDesign.deleteState(canvas, state);
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'state not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to delete state'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    entryCopyLink() {
      try {
        const res = commons.entryCopyLink({
          moduleName: this.module && this.module.name,
          activityId: this.selectedActivity && this.selectedActivity.id,
          documentId: this.header && this.header.documentId,
          title: this.header && this.header.title
        });

        this.$toasted.success(res.message);  
      } catch(err) {
        this.$toasted.global.error(err);
      }
    }
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    users() {
      return clientStore.getters.workspaceUsers;
    },
    states() {
      return this.designMode ?
        canvasDesignStore.getters.canvasStates :
        clientStore.getters.workspaceStates.filter((x) => x.module == this.moduleId);
    },
    module() {
      return clientStore.getters.workspaceModules.find((x) => x.id == this.moduleId);
    },
    isQuickViewRight() {
      return this.isRightOf('quickView');
    },
    isViewRight() {
      return this.isRightOf('view');
    },
    isCreateRight() {
      return this.isRightOf('create');
    },
    isCommentRight() {
      return this.isRightOf('comment');
    },
    isHistoryRight() {
      return this.isRightOf('history');
    },
    isAssignRight() {
      return this.isRightOf('assign');
    },
    isShareRight() {
      return this.isRightOf('share');
    },
    isChangeStatusRight() {
      return this.isRightOf('changeStatus');
    },
    isMultiple() {
      return this.entries && this.entries.length > 1;
    },
    isError() {
      return Boolean(
        (!Boolean(this.selectedActivity) && this.viewFormError) ||
        (Boolean(this.selectedActivity) && this.activityFormError)
      );
    },
    formActions() {
      let list = [];
      const addItems = (items, actionType) => {
        if (items && items.length) {
          const [lastItem] = list.slice(-1);

          if (lastItem && lastItem.actionType !== actionType && list.length) {
            list.push({
              id: `action-divider-${list.length}`,
              optionRole: "divider"
            });
          }

          const mappedItems = items.map(x => ({...x, actionType}));
          list = list.concat(mappedItems);
        }
      }

      if (!this.selectedActivity && this.footer) {
        addItems(this.footer.activities, "activity");
        addItems(this.footer.documents, "template");
        addItems(this.footer.customs, "custom");

        if (this.footer.standards && this.footer.standards.length) {
          let standards = commons.standardActivities.filter((x) =>
            this.footer.standards.indexOf(x.id) > -1 &&
            x.id != "view" &&
            x.id != "create" &&
            x.id != "changeStatus" &&
            x.id != "assign"
          );

          addItems(standards, "standard");
        }
      }

      if (this.isShareRight) {
        addItems([{id: "copyLink"}], "standard");
        addItems([{id: "share"}], "standard");

        if (authorizationStore.getters.isInternal) {
          addItems([{id: "embedForm"}], "standard");
        }
      }

      if (!this.selectedActivity && this.isAdminUser) {
        const availableAdminFeatures = ["manage"];
        let adminFeatures = commons.adminFeatures.filter(x => availableAdminFeatures.includes(x.id));
        addItems(adminFeatures, "standard");
      }

      return list;
    },
    formTitle() {
      if (
        (this.selectedActivity && this.selectedActivity.id !== 'create') ||
        !this.selectedActivity
      ) {
        if (this.isMultiple) {
          return `${this.entries.length} items selected`;
        } else if (this.header && this.header.title) {
          return this.header.title;
        } else if (!this.formLoading) {
          return "(untitled)";
        }
      }
    },
    selectedItemsEtc() {
      if (!this.entries || this.entries < 2) return "";
      else if (this.entries.length == 2) {
        return `${this.entries[0].documentId} and ${this.entries[1].documentId}`;
      } else if (this.entries.length == 3) {
        return `${this.entries[0].documentId}, ${this.entries[1].documentId} and ${this.entries[2].documentId}`;
      } else {
        return `${this.entries[0].documentId}, ${this.entries[1].documentId} and ${this.entries.length - 2} others`;
      }
    },
    canvasActivities() {
      return canvasDesignStore.getters.canvasActivities;
    }
  },
  async mounted() {
    Vue.prototype.$listingMode = 'detail';
    classificationStore.dispatch("resetActivityForm");
    window.eventBus.$on('loadDetailViewForm', async (res) => {
      const current = {
        id: this.header && this.header.id ? this.header.id : null,
        activityId: this.selectedActivity ? this.selectedActivity.id : null,
        moduleId: this.moduleId,
        fullView: this.fullView
      }

      if (
        res.fromUrl &&
        res.id == current.id &&
        res.activityId == current.activityId &&
        (!res.moduleId || res.moduleId == current.moduleId)
      ) {
        return;
      }

      this.viewFormError = null;
      this.activityFormError = null;
      this.moduleId = res.moduleId;
      this.listingId = res.listingId;
      this.username = res.username;
      this.fullView = Boolean(res.fullView);

      if (Array.isArray(res.entries) && res.entries.length) {
        this.setEntryHeader(res.entries);
      } else {
        this.setEntryHeader(res);
      }

      this.initialFormData();

      if (res.activityId && res.activityId !== 'view' && res.activityId !== 'quickView') {
        this.selectedActivity = commons.standardActivities.find(x => x.id == res.activityId);
        this.viewData = null;
        
        if (!this.selectedActivity) {
          this.selectedActivity = commons.adminFeatures.find(x => x.id == res.activityId);
        }
        
        if (!this.selectedActivity) {
          this.selectedActivity = { id: res.activityId };
        }
      } else {
        this.selectedActivity = null;
      }

      this.setRouter();

      if (this.selectedActivity && this.selectedActivity.id) {
        await this.loadActivityForm();
      } else {
        await this.loadViewForm();
      }
    });
  },
  beforeDestroy() {
    delete Vue.prototype.$listingMode;
    window.eventBus.$off('loadDetailViewForm');
  }
}
</script>

<style lang="scss" scoped>
@use "/scss/style.scss";
</style>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-detail-view-form {
  position: relative;
  display: flex;
  overflow: hidden;
  .dv-form {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .dv-form__header {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border-bottom: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
  }
  .dv-form__content {
    flex: 1;
    padding: 1rem 0.75rem 0.75rem;
    background-color: var(--fts-card-background);
    > * {
      min-width: 640px;
    }
  }
  .dv-form-header-activity {
    min-width: 0;
    margin-right: 0.625rem;
    color: var(--fts-text);
    font-size: 0.875rem;
  }
  .dv-form-header-activity-create {
    display: flex;
    align-items: center;
  }
  .dv-form-header-activity-create-label {
    margin-right: 0.5rem;
  }
  .dv-form-header {
    flex: 1;
    min-width: 0;
  }
  .dv-form-header-wrapper {
    display: flex;
    align-items: center;
    & + .dv-form-header-wrapper,
    & + .dv-form-header-activity{
      margin-top: 0.125rem;
    }
  }
  .dv-form-header-action {
    min-height: 2.1875rem;
    display: flex;
    align-items: center;
    margin-left: 0.625rem;
  }
  .dv-form-header-action-btn {
    font-size: 1rem;
  }
  .dv-form-title-wrapper {
    min-width: 0;
    &:not(:last-child) {
      margin-right: 0.625rem;
    }
  }
  .dv-form-title {
    @include text-overflow-ellipsis;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-text);
    cursor: default;
  }
  .dv-form-state-wrapper {
    min-width: 0;
    &:not(:last-child) {
      margin-right: 0.625rem;
    }
  }
  .dv-form-state {
    display: block;
  }
  .dv-form-header-action-item {
    height: 2.1875rem;
    min-width: 2.1875rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .dv-form-history {
    &.dv-form-history--count {
      position: relative;
      padding-right: 0.625rem;
    }
  }
  .dv-form-history-count {
    @include absolute-center-y;
    left: 1.625rem;
    min-width: 1rem;
    padding: 0 0.125rem;
    border: 1px solid transparent;
    border-radius: 10em;
    font-size: 0.75rem;
    color: var(--fts-default);
    text-align: center;
    transition: all 0.3s ease-in-out;
  }
  .dv-form-action-detail {
    margin-bottom: 1rem;
    text-align: center;
  }
  .dv-form-separator {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 1rem;
    color: var(--fts-text);
  }
  .dv-form-separator-line {
    flex: 1;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    margin-left: 1rem;
    border-top: 1px solid var(--fts-border);
  }
  .dv-form-activity-content {
    margin-bottom: 1.5rem;
  }
  .dv-form-activity-label {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-text);
  }
  .dv-form-activity-label-group {
    display: flex;
    align-items: center;
    .dv-form-activity-label {
      margin-right: 0.5rem;
    }
  }
  .dv-form-activity-state {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .dv-form-afooter {
    position: sticky;
    left: -0.75rem;
    bottom: -0.75rem;
    margin-left: -0.75rem;
    margin-right: -0.75rem;
    margin-bottom: -0.75rem;
    padding: 1.5rem 0.75rem 0.75rem;
    display: flex;
    align-items: flex-end;
    background-color: inherit;
    z-index: 1;
  }
  .dv-form-afooter-activity-flow {
    flex: 1 0 40%;
    max-width: 40%;
    min-height: 2.125rem;
    padding-right: 0.75rem;
  }
  .dv-form-afooter-action {
    flex: 1 0 60%;
    max-width: 60%;
  }
  .dv-form-error {
    padding: 2rem;
    text-align: center;
  }
  .dv-form-error-icon {
    height: 80px;
    width: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.25rem;
    border: 2px solid var(--fts-danger);
    border-radius: 50%;
    color: var(--fts-danger);
    > svg {
      color: inherit;
      font-size: 2.5rem;
    }
  }
  .dv-form-error-title {
    margin-bottom: 0.75rem;
    color: var(--fts-danger);
    font-weight: 500;
    font-size: 1.25rem;
    line-height: 1.75rem;
    word-break: break-word;
  }
  .dv-form-error-description {
    color: var(--fts-default);
    font-size: 0.875rem;
    line-height: 1.25rem;
    word-break: break-word;
  }
  .dv-form-loading-overlay {
    @include flex-center-middle;
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: transparent;
    z-index: 1;
    cursor: wait;
  }
  .dv-form-loading-offset-r {
    margin-right: 325px;
  }
  .fts-history {
    flex: 1 1 325px !important;
    min-width: 325px !important;
    max-width: 325px !important;
  }
}
</style>